import { useState } from 'react';
import { Building2, Plus, ListChecks } from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Skeleton from '../../components/ui/Skeleton';
import { useShop } from '../../hooks/useShop';
import { useAuth } from '../../contexts/AuthContext';
import { useUnprofiledVendors } from '../../hooks/useVendorData';
import { api } from '../../api/client';

export default function VendorsNeededPage() {
  const { currentShopId, currentShop, shops } = useShop();
  const { isAllShops } = useAuth();
  // For all-shops users, default to no filter (see all). Scoped users see their shop.
  const [shopFilter, setShopFilter] = useState(isAllShops ? '' : (currentShopId || ''));
  const effectiveShopId = shopFilter || (isAllShops ? null : currentShopId);
  const { vendors, loading, refresh } = useUnprofiledVendors(effectiveShopId);
  const [creating, setCreating] = useState(null);  // payee string

  return (
    <div>
      <PageHeader
        title="Vendors Needing Profile"
        description={`${shopFilter ? (shops.find(s => s.shop_id === Number(shopFilter))?.shop_nickname || 'Shop') : 'All shops'} · ${vendors.length} unique vendor names detected in scans without an approved profile.`}
        actions={
          <Button variant="ghost" onClick={refresh}>Refresh</Button>
        }
      />

      {isAllShops && shops.length > 0 && (
        <div className="flex items-center gap-3 mb-4 text-[13px]">
          <span className="text-[var(--color-warm-500)] font-semibold uppercase text-[10px] tracking-wide">Shop:</span>
          <select
            value={shopFilter}
            onChange={(e) => setShopFilter(e.target.value)}
            className="h-9 px-3 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] text-[13px] font-semibold text-[var(--color-warm-800)] focus:outline-none focus:border-[var(--color-insight-500)]"
          >
            <option value="">All shops</option>
            {shops.map((s) => (
              <option key={s.shop_id} value={s.shop_id}>
                {s.shop_nickname || s.shop_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => <Skeleton key={i} height={70} />)}
        </div>
      ) : vendors.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <ListChecks size={36} className="text-[var(--color-success-500)] mx-auto mb-2" />
            <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">All vendors profiled</div>
            <div className="text-[12px] text-[var(--color-warm-500)] mt-1">
              Every vendor seen in scans has an approved profile.
            </div>
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <ul className="divide-y divide-[var(--color-warm-100)]">
            {vendors.map((v, i) => (
              <li key={`${v.shop_id}-${v.payee}-${i}`} className="px-5 py-3 flex items-center gap-4 hover:bg-[var(--color-cream-100)] transition-colors">
                <div className="w-9 h-9 rounded-[var(--radius-sm)] bg-[var(--color-warm-100)] flex items-center justify-center text-[var(--color-warm-600)] shrink-0">
                  <Building2 size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold text-[var(--color-warm-900)]">{v.payee}</div>
                  <div className="text-[11px] text-[var(--color-warm-500)] mt-0.5 flex items-center gap-2">
                    <span>{v.shop_nickname || 'shop ' + v.shop_id}</span>
                    <span>·</span>
                    <span>seen <strong>{v.occurrence_count}×</strong> in scans</span>
                  </div>
                </div>
                <Badge tone="action" size="xs">No profile</Badge>
                <Button variant="primary" size="sm" leftIcon={<Plus size={14} />} onClick={() => setCreating(v)}>
                  Create profile
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {creating && (
        <CreateProfileFromVendorModal
          payee={creating.payee}
          shopId={creating.shop_id}
          onClose={() => setCreating(null)}
          onCreated={() => { setCreating(null); refresh(); }}
        />
      )}
    </div>
  );
}

function CreateProfileFromVendorModal({ payee, shopId, onClose, onCreated }) {
  const [form, setForm] = useState({
    canonical_name: payee,
    aliases: payee !== payee.toLowerCase() ? payee.toLowerCase() : '',
    vendor_category: 'GENERAL_AUTO',
    payment_method: 'COD',
    statement_cadence: '',
    is_parts_vendor: true,
    notes: `Auto-created from scan extraction: "${payee}"`,
  });
  const [submitImmediately, setSubmitImmediately] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.createVendorProfile({
        ...form,
        shop_id: shopId,
        aliases: form.aliases.split(',').map((s) => s.trim()).filter(Boolean),
        statement_cadence: form.statement_cadence || null,
        submit_immediately: submitImmediately,
      });
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`New profile for "${payee}"`}
      description="Set up the basics; aliases and details can be tuned later."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={save} loading={saving}>
            {submitImmediately ? 'Create & Submit' : 'Save as Draft'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Canonical name"
          value={form.canonical_name}
          onChange={(e) => setForm({ ...form, canonical_name: e.target.value })}
          required
          className="col-span-2"
        />
        <Input
          label="Aliases (comma-separated)"
          value={form.aliases}
          onChange={(e) => setForm({ ...form, aliases: e.target.value })}
          hint="Other spellings the AI might extract"
          className="col-span-2"
        />
        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Category</label>
          <select
            value={form.vendor_category}
            onChange={(e) => setForm({ ...form, vendor_category: e.target.value })}
            className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
          >
            <option value="GENERAL_AUTO">General Automotive</option>
            <option value="TRANSMISSION">Transmission</option>
            <option value="SHOP_SUPPLIES">Shop Supplies</option>
            <option value="SUBLET">Sublet</option>
            <option value="IGNORED">Ignored (lawn, deposit, etc.)</option>
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Payment</label>
          <select
            value={form.payment_method}
            onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
            className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
          >
            <option value="COD">COD</option>
            <option value="STATEMENT">Statement</option>
            <option value="MIXED">Mixed</option>
          </select>
        </div>
        {form.payment_method === 'STATEMENT' && (
          <div className="col-span-2">
            <label className="block text-[12px] font-semibold text-[var(--color-warm-700)] mb-1.5">Statement Cadence</label>
            <select
              value={form.statement_cadence}
              onChange={(e) => setForm({ ...form, statement_cadence: e.target.value })}
              className="w-full h-10 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 text-[14px]"
            >
              <option value="">— Select —</option>
              <option value="WEEKLY">Weekly</option>
              <option value="BIWEEKLY">Biweekly</option>
              <option value="MONTHLY">Monthly</option>
              <option value="ON_DEMAND">On Demand</option>
            </select>
          </div>
        )}
        <label className="col-span-2 flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={submitImmediately}
            onChange={(e) => setSubmitImmediately(e.target.checked)}
            className="w-4 h-4"
          />
          Submit immediately for auditor approval
        </label>
        {error && (
          <div className="col-span-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[13px] text-[var(--color-danger-700)]">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
